import { useState, useEffect, useCallback } from 'react';
import { getUserProfile } from '../../../services/userService';
import { USER_ROLES } from '../constants';

interface UseAdminAuthReturn {
  isAdmin: boolean | null;
  isSuperAdmin: boolean | null;
  isLoading: boolean;
  error: string | null;
  checkAdminStatus: () => Promise<void>;
}

/**
 * Hook to check and manage admin authentication status
 *
 * GRANULAR ACCESS: Now uses workspace permission system (is_workspace_admin)
 * which grants access if user has ANY of the following:
 * - can_admin: Full admin privileges (ALL permissions)
 * - can_view_analytics: View analytics dashboard
 * - can_manage_agents: Manage agents, knowledge bases, data sources
 * - can_manage_workspace: Manage workspace members and groups
 * - Legacy admin/superadmin role (backward compatibility)
 *
 * Users only see admin menu items they have permission for (filtered in Sidebar).
 */
export const useAdminAuth = (): UseAdminAuthReturn => {
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const checkAdminStatus = useCallback(async () => {
    const token = localStorage.getItem('token');
    const refreshToken = localStorage.getItem('refreshToken');

    if (!token || !refreshToken) {
      setIsAdmin(false);
      setIsSuperAdmin(false);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const response = await getUserProfile();
      const roleName = typeof response.data.role === 'string'
        ? response.data.role
        : response.data.role.name;

      // NEW: Use workspace admin check (includes legacy roles as fallback)
      const isWorkspaceAdmin = response.data.is_workspace_admin || false;

      // LEGACY: Still check role for superadmin-specific features
      const isSuperAdminRole = roleName === USER_ROLES.superadmin;

      setIsAdmin(isWorkspaceAdmin); // Uses NEW system
      setIsSuperAdmin(isSuperAdminRole); // Keeps legacy for specific features
    } catch (err) {
      console.error('Error checking admin status:', err);
      setError('Erro ao verificar permissões de administrador');
      setIsAdmin(false);
      setIsSuperAdmin(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAdminStatus();
  }, [checkAdminStatus]);

  return {
    isAdmin,
    isSuperAdmin,
    isLoading,
    error,
    checkAdminStatus,
  };
};
